import { supabase } from '../lib/supabase';

export const realtimeViolationService = {
  // ============================================================================
  // REAL-TIME SUBSCRIPTION FOR VIOLATIONS
  // ============================================================================

  subscribeToViolations(callback) {
    const channel = supabase
      ?.channel('realtime-violations')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'compliance_violations'
        },
        (payload) => {
          const violation = this.transformViolation(payload?.new);
          callback(violation, payload?.eventType);
        }
      )
      ?.subscribe();

    return channel;
  },

  subscribeToComplianceScores(callback) {
    const channel = supabase
      ?.channel('realtime-compliance-scores')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'compliance_reports'
        },
        (payload) => {
          const report = this.transformComplianceReport(payload?.new);
          callback(report, payload?.eventType);
        }
      )
      ?.subscribe();

    return channel;
  },

  subscribeToViolationDetectionLog(callback) {
    const channel = supabase
      ?.channel('realtime-violation-detection-log')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'violation_detection_log'
        },
        (payload) => {
          const detectionLog = this.transformDetectionLog(payload?.new);
          callback(detectionLog);
        }
      )
      ?.subscribe();

    return channel;
  },

  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  },

  // ============================================================================
  // DATA TRANSFORMATION
  // ============================================================================

  transformViolation(row) {
    if (!row) return null;
    
    return {
      id: row?.id,
      violationType: row?.violation_type,
      severity: row?.severity,
      violationStatus: row?.violation_status,
      description: row?.description,
      department: row?.department,
      affectedCount: row?.affected_count,
      affectedEmployeeIds: row?.affected_employee_ids || [],
      estimatedCost: row?.estimated_cost,
      regulationReference: row?.regulation_reference,
      correctiveAction: row?.corrective_action,
      identifiedAt: row?.identified_at,
      resolvedAt: row?.resolved_at,
      identifiedBy: row?.identified_by,
      assignedTo: row?.assigned_to,
      metadata: row?.metadata,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    };
  },

  transformComplianceReport(row) {
    if (!row) return null;
    
    return {
      id: row?.id,
      reportName: row?.report_name,
      reportPeriodType: row?.report_period_type,
      periodStart: row?.period_start,
      periodEnd: row?.period_end,
      reportStatus: row?.report_status,
      overallComplianceScore: row?.overall_compliance_score,
      previousPeriodScore: row?.previous_period_score,
      variancePercentage: row?.variance_percentage,
      varianceType: row?.variance_type,
      totalViolations: row?.total_violations,
      criticalViolations: row?.critical_violations,
      resolvedViolations: row?.resolved_violations,
      totalEmployees: row?.total_employees,
      compliantEmployees: row?.compliant_employees,
      reportSummary: row?.report_summary,
      generatedBy: row?.generated_by,
      generatedAt: row?.generated_at,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    };
  },

  transformDetectionLog(row) {
    if (!row) return null;
    
    return {
      id: row?.id,
      violationId: row?.violation_id,
      triggerType: row?.trigger_type,
      detectionSource: row?.detection_source,
      detectionTimestamp: row?.detection_timestamp,
      ruleViolated: row?.rule_violated,
      expectedValue: row?.expected_value,
      actualValue: row?.actual_value,
      varianceAmount: row?.variance_amount,
      variancePercentage: row?.variance_percentage,
      affectedEmployeeIds: row?.affected_employee_ids || [],
      affectedPayrollRunId: row?.affected_payroll_run_id,
      affectedDepartment: row?.affected_department,
      alertSent: row?.alert_sent,
      alertSentAt: row?.alert_sent_at,
      adminNotified: row?.admin_notified,
      clientNotified: row?.client_notified,
      confidenceScore: row?.confidence_score,
      detectionMethod: row?.detection_method,
      metadata: row?.metadata,
      createdAt: row?.created_at
    };
  },

  // ============================================================================
  // INSTANT ALERT NOTIFICATIONS
  // ============================================================================

  async getLatestViolations(limit = 50) {
    try {
      const { data, error } = await supabase
        ?.from('compliance_violations')
        ?.select('*')
        ?.order('identified_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;

      return data?.map(row => this.transformViolation(row)) || [];
    } catch (error) {
      console.error('Error fetching latest violations:', error);
      throw error;
    }
  },

  async getLatestComplianceScore() {
    try {
      const { data, error } = await supabase
        ?.from('compliance_reports')
        ?.select('*')
        ?.order('created_at', { ascending: false })
        ?.limit(1)
        ?.single();

      if (error) throw error;

      return this.transformComplianceReport(data);
    } catch (error) {
      console.error('Error fetching latest compliance score:', error);
      throw error;
    }
  }
};

export default realtimeViolationService;
